import {
	createPhoneNumber,
	deletePhoneNumber,
	logAction,
	updatePhoneNumber,
	useAuth,
} from "@repo/ui";
import { getFirestore } from "firebase/firestore";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { PhoneNumber } from "../types";
import { TypedDeleteConfirmationDialog } from "./TypedDeleteConfirmationDialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";

type PhoneNumberChannel = "live" | "test" | "unassigned";
type PhoneNumberProvider = "twilio" | "meta";

function normalizePhoneNumber(value: string): string | null {
	const cleaned = value.trim().replace(/[^\d+]/g, "");
	if (!cleaned) return null;

	if (cleaned.startsWith("+")) {
		const digits = cleaned.slice(1).replace(/\D/g, "");
		return digits ? `+${digits}` : null;
	}

	const digits = cleaned.replace(/\D/g, "");
	if (!digits) return null;

	if (digits.startsWith("00")) {
		const internationalDigits = digits.slice(2);
		return internationalDigits ? `+${internationalDigits}` : null;
	}

	if (digits.startsWith("972")) return `+${digits}`;
	if (digits.startsWith("0")) return `+972${digits.slice(1)}`;
	return `+${digits}`;
}

export function PhoneNumbersPage() {
	const { user } = useAuth();
	const { phoneNumbers, companies, agents } = useData();
	const db = getFirestore();
	const [searchTerm, setSearchTerm] = useState("");
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [selectedPhoneNumber, setSelectedPhoneNumber] =
		useState<PhoneNumber | null>(null);
	const [formData, setFormData] = useState({
		number: "",
		provider: "twilio" as PhoneNumberProvider,
		channel: "unassigned" as PhoneNumberChannel,
		status: "active" as "active" | "disabled",
		agentId: "",
		companyId: "",
		cost: "100", // in cents
	});

	const filteredPhoneNumbers = useMemo(() => {
		if (!searchTerm) return phoneNumbers;

		return phoneNumbers.filter((phone) => {
			const company = companies.find((c) => c.id === phone.companyId);
			const agent = agents.find((a) => a.id === phone.agentId);

			return (
				phone.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
				phone.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
				(phone.channel || "unassigned")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				(phone.status || "active")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				(phone.agentSync?.status || "pending")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				(phone.agentSync?.error || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				company?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				agent?.id.toLowerCase().includes(searchTerm.toLowerCase())
			);
		});
	}, [phoneNumbers, companies, agents, searchTerm]);

	const formatDate = (date: Date | string | null | undefined) => {
		if (!date) return "Never";
		if (typeof date === "string") return new Date(date).toLocaleString();
		return date.toLocaleString();
	};

	const formatCost = (cents: number) => {
		return `$${(cents / 100).toFixed(2)}`;
	};

	const isLiveNumberMissingAgent =
		formData.channel === "live" && !formData.agentId;
	const agentRequirementMessage =
		"Live phone numbers must have an agent assigned.";

	const handleChannelChange = (channel: PhoneNumberChannel) => {
		const newFormData = { ...formData, channel };
		if (channel !== "live") {
			newFormData.agentId = "";
			newFormData.companyId = "";
		}
		setFormData(newFormData);
	};

	const handleAgentChange = (agentId: string) => {
		const normalizedAgentId = agentId === "none" ? "" : agentId;
		const selectedAgent = agents.find(
			(agent) => agent.id === normalizedAgentId,
		);
		setFormData({
			...formData,
			agentId: normalizedAgentId,
			companyId: selectedAgent?.companyId || "",
		});
	};

	const handleAddSubmit = async () => {
		// Validate form
		if (!formData.number || !formData.provider || !formData.cost) {
			toast.error("Please fill in all required fields");
			return;
		}

		if (isLiveNumberMissingAgent) {
			toast.error(agentRequirementMessage);
			return;
		}
		const normalizedNumber = normalizePhoneNumber(formData.number);
		if (!normalizedNumber) {
			toast.error("Please enter a valid phone number");
			return;
		}

		try {
			const { cost, ...phoneNumberData } = formData;
			const isLive = formData.channel === "live";
			await createPhoneNumber(db, {
				...phoneNumberData,
				number: normalizedNumber,
				agentId: isLive ? formData.agentId || null : null,
				companyId: isLive ? formData.companyId || null : null,
				monthlyCostInCents: parseInt(cost, 10),
			});
			toast.success(`Phone number "${normalizedNumber}" created successfully`);

			if (user?.uid && formData.companyId) {
				await logAction(db, {
					userId: user.uid,
					type: "phone_number_created",
					phoneNumber: normalizedNumber,
					companyId: formData.companyId,
					data: {
						config: {
							...phoneNumberData,
							number: normalizedNumber,
							monthlyCostInCents: parseInt(cost, 10),
						},
					},
				});

				if (formData.agentId) {
					await logAction(db, {
						userId: user.uid,
						type: "phone_number_assigned",
						phoneNumber: normalizedNumber,
						agentId: formData.agentId,
						companyId: formData.companyId,
					});
				}
			}

			// Reset form and close dialog
			setFormData({
				number: "",
				provider: "twilio",
				channel: "unassigned",
				status: "active",
				agentId: "",
				companyId: "",
				cost: "100",
			});
			setIsAddDialogOpen(false);
		} catch (error) {
			console.error("Failed to create phone number", error);
			toast.error("Failed to create phone number");
		}
	};

	const handleEditClick = (phoneNumber: PhoneNumber) => {
		setSelectedPhoneNumber(phoneNumber);
		setFormData({
			number: phoneNumber.number,
			provider: phoneNumber.provider === "meta" ? "meta" : "twilio",
			channel: phoneNumber.channel || "unassigned",
			status: phoneNumber.status || "active",
			agentId: phoneNumber.agentId || "",
			companyId: phoneNumber.companyId || "",
			cost: (phoneNumber.monthlyCostInCents || 0).toString(),
		});
		setIsEditDialogOpen(true);
	};

	const handleEditSubmit = async () => {
		if (!selectedPhoneNumber || !selectedPhoneNumber.id) return;

		// Validate form
		if (!formData.number || !formData.provider || !formData.cost) {
			toast.error("Please fill in all required fields");
			return;
		}

		if (isLiveNumberMissingAgent) {
			toast.error(agentRequirementMessage);
			return;
		}
		const normalizedNumber = normalizePhoneNumber(formData.number);
		if (!normalizedNumber) {
			toast.error("Please enter a valid phone number");
			return;
		}

		try {
			const { cost, ...phoneNumberData } = formData;
			const oldAgentId = selectedPhoneNumber.agentId;
			const isLive = formData.channel === "live";
			const newAgentId = isLive ? formData.agentId || null : null;
			const newCompanyId = isLive ? formData.companyId || null : null;

			await updatePhoneNumber(db, selectedPhoneNumber.id, {
				...phoneNumberData,
				number: normalizedNumber,
				agentId: newAgentId,
				companyId: newCompanyId,
				monthlyCostInCents: parseInt(cost, 10),
			});
			toast.success(`Phone number "${normalizedNumber}" updated successfully`);

			if (user?.uid) {
				if (formData.companyId) {
					await logAction(db, {
						userId: user.uid,
						type: "phone_number_updated",
						phoneNumber: normalizedNumber,
						companyId: formData.companyId,
						data: {
							updates: {
								...phoneNumberData,
								number: normalizedNumber,
								monthlyCostInCents: parseInt(cost, 10),
							},
						},
					});
				}

				if (oldAgentId !== newAgentId) {
					if (oldAgentId && selectedPhoneNumber.companyId) {
						await logAction(db, {
							userId: user.uid,
							type: "phone_number_unassigned",
							phoneNumber: normalizedNumber,
							agentId: oldAgentId,
							companyId: selectedPhoneNumber.companyId,
						});
					}
					if (newAgentId && formData.companyId) {
						await logAction(db, {
							userId: user.uid,
							type: "phone_number_assigned",
							phoneNumber: normalizedNumber,
							agentId: newAgentId,
							companyId: formData.companyId,
						});
					}
				}
			}

			setIsEditDialogOpen(false);
			setSelectedPhoneNumber(null);
		} catch (error) {
			console.error("Failed to update phone number", error);
			toast.error("Failed to update phone number");
		}
	};

	const handleDeleteClick = (phoneNumber: PhoneNumber) => {
		setSelectedPhoneNumber(phoneNumber);
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedPhoneNumber || !selectedPhoneNumber.id) return;

		try {
			await deletePhoneNumber(db, selectedPhoneNumber.id);
			toast.success(
				`Phone number "${selectedPhoneNumber.number}" deleted successfully`,
			);

			if (user?.uid && selectedPhoneNumber.companyId) {
				await logAction(db, {
					userId: user.uid,
					type: "phone_number_deleted",
					phoneNumber: selectedPhoneNumber.number,
					companyId: selectedPhoneNumber.companyId,
				});
			}
			setIsDeleteDialogOpen(false);
			setSelectedPhoneNumber(null);
		} catch (error) {
			console.error("Failed to delete phone number", error);
			toast.error("Failed to delete phone number");
		}
	};

	const getCompanyName = (companyId: string | null | undefined) => {
		if (!companyId) return <span className="text-slate-400">Unassigned</span>;
		const company = companies.find((c) => c.id === companyId);
		return company?.name || "Unknown";
	};

	const getAgentName = (agentId: string | null | undefined) => {
		if (!agentId) return <span className="text-slate-400">Unassigned</span>;
		return agents.find((agent) => agent.id === agentId)?.name || agentId;
	};

	const getChannelBadge = (channel: PhoneNumberChannel) => {
		if (channel === "test") {
			return <Badge className="bg-blue-100 text-blue-800">Test</Badge>;
		}

		if (channel === "live") {
			return <Badge className="bg-green-100 text-green-800">Live</Badge>;
		}

		return <Badge className="bg-slate-100 text-slate-800">Unassigned</Badge>;
	};

	const getStatusBadge = (status: "active" | "disabled") => {
		if (status === "disabled") {
			return <Badge className="bg-red-100 text-red-800">Disabled</Badge>;
		}

		return <Badge className="bg-slate-100 text-slate-800">Active</Badge>;
	};

	const getAgentSyncBadge = (phone: {
		agentSync?: {
			status?: string;
			error?: string | null;
			syncedAt?: Date | string | null;
		};
	}) => {
		if (phone.agentSync?.status === "completed") {
			return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
		}

		if (phone.agentSync?.status === "error") {
			return (
				<Badge
					className="bg-red-100 text-red-800"
					title={phone.agentSync.error || undefined}
				>
					Error
				</Badge>
			);
		}

		if (phone.agentSync?.status === "skipped") {
			return <Badge className="bg-slate-100 text-slate-800">Skipped</Badge>;
		}

		return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
	};

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-start">
				<div>
					<h1>Phone Numbers</h1>
					<p className="text-slate-600">
						Manage phone numbers and their assignments
					</p>
				</div>
				<Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
					<DialogTrigger asChild>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							Add Phone Number
						</Button>
					</DialogTrigger>
					<DialogContent className="admin-editor-dialog">
						<DialogHeader>
							<DialogTitle>Add New Phone Number</DialogTitle>
							<DialogDescription>
								Register a new phone number in the system.
							</DialogDescription>
						</DialogHeader>
						<div className="admin-editor-body admin-editor-grid">
							<div className="space-y-2">
								<Label htmlFor="phone-number">Phone Number *</Label>
								<Input
									id="phone-number"
									placeholder="+1 (555) 123-4567"
									value={formData.number}
									onChange={(e) =>
										setFormData({ ...formData, number: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="provider">Provider *</Label>
								<Select
									value={formData.provider}
									onValueChange={(value) =>
										setFormData({
											...formData,
											provider: value as PhoneNumberProvider,
										})
									}
								>
									<SelectTrigger id="provider">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="twilio">Twilio</SelectItem>
										<SelectItem value="meta">Meta</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="channel">Channel *</Label>
								<Select
									value={formData.channel}
									onValueChange={handleChannelChange}
								>
									<SelectTrigger id="channel">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="live">Live</SelectItem>
										<SelectItem value="test">Test</SelectItem>
										<SelectItem value="unassigned">Unassigned</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="status">Status *</Label>
								<Select
									value={formData.status}
									onValueChange={(value: "active" | "disabled") =>
										setFormData({ ...formData, status: value })
									}
								>
									<SelectTrigger id="status">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="active">Active</SelectItem>
										<SelectItem value="disabled">Disabled</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="agent">
									Agent {formData.channel === "live" ? "*" : "(Optional)"}
								</Label>
								<Select
									value={formData.agentId || undefined}
									onValueChange={handleAgentChange}
									disabled={formData.channel !== "live"}
								>
									<SelectTrigger id="agent">
										<SelectValue
											placeholder={
												formData.channel === "live"
													? "Select an agent"
													: "Select an agent (optional)"
											}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="none">Unassigned</SelectItem>
										{agents.map((agent) => (
											<SelectItem key={agent.id} value={agent.id}>
												{agent.name} ({agent.id})
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{isLiveNumberMissingAgent && (
									<p className="text-sm text-red-600">
										{agentRequirementMessage}
									</p>
								)}
							</div>
							<div className="space-y-2">
								<Label htmlFor="company">Company (Optional)</Label>
								<Input
									id="company"
									value={
										formData.companyId
											? companies.find(
													(company) => company.id === formData.companyId,
												)?.name || formData.companyId
											: "Unassigned"
									}
									disabled
									className="bg-slate-100 cursor-not-allowed"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="cost">Monthly Cost (cents) *</Label>
								<Input
									id="cost"
									type="number"
									placeholder="100"
									value={formData.cost}
									onChange={(e) =>
										setFormData({ ...formData, cost: e.target.value })
									}
								/>
								<p className="text-sm text-slate-500">
									Enter cost in cents (e.g., 100 = $1.00)
								</p>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsAddDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button onClick={handleAddSubmit}>Create Phone Number</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Edit Dialog */}
			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="admin-editor-dialog">
					<DialogHeader>
						<DialogTitle>Edit Phone Number</DialogTitle>
						<DialogDescription>
							Update phone number details. Number, created date, and provider
							are read-only.
						</DialogDescription>
					</DialogHeader>
					<div className="admin-editor-body admin-editor-grid">
						<div className="space-y-2">
							<Label htmlFor="edit-phone-number">Phone Number</Label>
							<Input
								id="edit-phone-number"
								value={formData.number}
								disabled
								className="bg-slate-100 cursor-not-allowed"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-provider">Provider</Label>
							<Input
								id="edit-provider"
								value={formData.provider}
								disabled
								className="bg-slate-100 cursor-not-allowed"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-channel">Channel</Label>
							<Select
								value={formData.channel}
								onValueChange={handleChannelChange}
							>
								<SelectTrigger id="edit-channel">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="live">Live</SelectItem>
									<SelectItem value="test">Test</SelectItem>
									<SelectItem value="unassigned">Unassigned</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-status">Status</Label>
							<Select
								value={formData.status}
								onValueChange={(value: "active" | "disabled") =>
									setFormData({ ...formData, status: value })
								}
							>
								<SelectTrigger id="edit-status">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="active">Active</SelectItem>
									<SelectItem value="disabled">Disabled</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-agent">
								Agent {formData.channel === "live" ? "*" : "(Optional)"}
							</Label>
							<Select
								value={formData.agentId || undefined}
								onValueChange={handleAgentChange}
								disabled={formData.channel !== "live"}
							>
								<SelectTrigger id="edit-agent">
									<SelectValue
										placeholder={
											formData.channel === "live"
												? "Select an agent"
												: "Select an agent (optional)"
										}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="none">Unassigned</SelectItem>
									{agents.map((agent) => (
										<SelectItem key={agent.id} value={agent.id}>
											{agent.name} ({agent.id})
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{isLiveNumberMissingAgent && (
								<p className="text-sm text-red-600">
									{agentRequirementMessage}
								</p>
							)}
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-company">Company</Label>
							<Input
								id="edit-company"
								value={
									formData.companyId
										? companies.find(
												(company) => company.id === formData.companyId,
											)?.name || formData.companyId
										: "Unassigned"
								}
								disabled
								className="bg-slate-100 cursor-not-allowed"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="edit-cost">Monthly Cost (cents) *</Label>
							<Input
								id="edit-cost"
								type="number"
								placeholder="100"
								value={formData.cost}
								onChange={(e) =>
									setFormData({ ...formData, cost: e.target.value })
								}
							/>
							<p className="text-sm text-slate-500">
								Enter cost in cents (e.g., 100 = $1.00)
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button onClick={handleEditSubmit}>Save Changes</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<TypedDeleteConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				entityType="Phone Number"
				entityName={selectedPhoneNumber?.number ?? ""}
				consequences="This action cannot be undone and may interrupt production call routing."
				onConfirm={handleDeleteConfirm}
			/>

			<Card>
				<CardHeader>
					<CardTitle>All Phone Numbers</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="mb-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
							<Input
								placeholder="Search by number, provider, channel, status, sync, company, agent..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
					</div>

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Phone Number</TableHead>
								<TableHead>Provider</TableHead>
								<TableHead>Channel</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Agent Sync</TableHead>
								<TableHead>Company</TableHead>
								<TableHead>Agent</TableHead>
								<TableHead>Last Call</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Monthly Cost</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredPhoneNumbers.map((phone) => (
								<TableRow key={phone.id}>
									<TableCell>{phone.number}</TableCell>
									<TableCell>
										<Badge variant="outline">{phone.provider}</Badge>
									</TableCell>
									<TableCell>{getChannelBadge(phone.channel)}</TableCell>
									<TableCell>{getStatusBadge(phone.status)}</TableCell>
									<TableCell>{getAgentSyncBadge(phone)}</TableCell>
									<TableCell>{getCompanyName(phone.companyId)}</TableCell>
									<TableCell>{getAgentName(phone.agentId)}</TableCell>
									<TableCell>{formatDate(phone.lastCallAt)}</TableCell>
									<TableCell>{formatDate(phone.createdAt)}</TableCell>
									<TableCell>
										{formatCost(phone.monthlyCostInCents || 0)}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												className="min-h-11 min-w-11"
												aria-label={`Edit ${phone.number}`}
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleEditClick(phone);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="min-h-11 min-w-11"
												aria-label={`Delete ${phone.number}`}
												onClick={(e: React.MouseEvent) => {
													e.stopPropagation();
													handleDeleteClick(phone);
												}}
											>
												<Trash2 className="h-4 w-4 text-red-600" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{filteredPhoneNumbers.length === 0 && (
						<div className="text-center py-8 text-slate-500">
							No phone numbers found
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
